import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Download, Mail, Copy, MessageSquare, 
  User, MapPin, Phone, Package, Ruler, Wrench, 
  Square, Truck, AlertTriangle, Check, Send, Edit2,
  Calendar, Plus, Clock, Trash2, X, FileText, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusOptions = [
  { value: 'brouillon', label: 'Brouillon' },
  { value: 'envoyé', label: 'Envoyé' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'validé', label: 'Validé' },
  { value: 'en_production', label: 'En production' },
  { value: 'terminé', label: 'Terminé' },
  { value: 'annulé', label: 'Annulé' },
];

// Updated event types - merged Livraison + Pose
const EVENT_TYPES = [
  { value: 'mesure', label: 'Relevé de mesures', color: '#3B82F6' },
  { value: 'livraison_pose', label: 'Livraison & Pose', color: '#10B981' },
  { value: 'autre', label: 'Autre', color: '#8B5CF6' },
];

const STATUS_LABELS = {
  proposed: 'Proposée',
  validated: 'Validée',
  modified_by_admin: 'Modifiée'
};

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, user } = useAuth();
  const isAdminView = location.pathname.startsWith('/admin');
  
  const [quote, setQuote] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Planning state
  const [planningEvents, setPlanningEvents] = useState([]);
  const [showPlanningDialog, setShowPlanningDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    event_type: 'mesure',
    event_label: '',
    proposed_date: '',
    proposed_time: '',
    comment: ''
  });

  useEffect(() => {
    fetchQuote();
    fetchMessages();
    fetchPlanningEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}`);
      setQuote(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du devis');
      navigate(isAdminView ? '/admin/devis' : '/dashboard/devis');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/messages/${id}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchPlanningEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/planning/events/quote/${id}`);
      setPlanningEvents(response.data);
    } catch (error) {
      console.error('Error fetching planning:', error);
    }
  };

  const resetEventForm = () => {
    setNewEvent({
      event_type: 'mesure',
      event_label: '',
      proposed_date: '',
      proposed_time: '',
      comment: ''
    });
    setEditingEvent(null);
  };

  const handleAddEvent = async () => {
    if (!newEvent.proposed_date) {
      toast.error('Veuillez sélectionner une date');
      return;
    }
    
    if (newEvent.event_type === 'autre' && !newEvent.event_label.trim()) {
      toast.error('Veuillez préciser le type d\'événement');
      return;
    }
    
    try {
      // Combine date and time
      let proposed_date = newEvent.proposed_date;
      if (newEvent.proposed_time) {
        proposed_date = `${newEvent.proposed_date}T${newEvent.proposed_time}:00`;
      }
      
      await axios.post(`${API_URL}/planning/events`, {
        quote_id: id,
        event_type: newEvent.event_type,
        event_label: newEvent.event_label,
        proposed_date,
        comment: newEvent.comment
      });
      toast.success('Date proposée');
      setShowPlanningDialog(false);
      resetEventForm();
      fetchPlanningEvents();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;
    
    try {
      // Combine date and time
      let validated_date = newEvent.proposed_date;
      if (newEvent.proposed_time) {
        validated_date = `${newEvent.proposed_date}T${newEvent.proposed_time}:00`;
      }
      
      await axios.put(`${API_URL}/planning/events/${editingEvent.id}`, {
        validated_date,
        event_label: newEvent.event_label,
        comment: newEvent.comment,
        status: isAdminView ? 'modified_by_admin' : editingEvent.status
      });
      toast.success('Événement modifié');
      setShowPlanningDialog(false);
      resetEventForm();
      fetchPlanningEvents();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) return;
    
    try {
      await axios.delete(`${API_URL}/planning/events/${eventId}`);
      toast.success('Événement supprimé');
      fetchPlanningEvents();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (event) => {
    const dateStr = event.validated_date || event.proposed_date || '';
    const datePart = dateStr.split('T')[0] || '';
    const timePart = dateStr.includes('T') ? dateStr.split('T')[1]?.substring(0, 5) : '';
    
    setEditingEvent(event);
    setNewEvent({
      event_type: event.event_type,
      event_label: event.event_label || '',
      proposed_date: datePart,
      proposed_time: timePart,
      comment: event.comment || ''
    });
    setShowPlanningDialog(true);
  };

  const handleValidateEvent = async (eventId) => {
    try {
      await axios.put(`${API_URL}/planning/events/${eventId}`, {
        status: 'validated'
      });
      toast.success('Date validée');
      fetchPlanningEvents();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API_URL}/quotes/${id}/status`, { status: newStatus });
      setQuote({ ...quote, status: newStatus });
      toast.success('Statut mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Client PRO validation
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  
  const handleClientValidation = async () => {
    try {
      await axios.put(`${API_URL}/quotes/${id}/status`, { status: 'validé' });
      setQuote({ ...quote, status: 'validé' });
      toast.success('Devis validé ! GRANIPAL va prendre contact avec vous pour la suite.');
      setShowValidationDialog(false);
    } catch (error) {
      toast.error('Erreur lors de la validation du devis');
    }
  };

  const canClientValidate = !isAdminView && quote?.status !== 'validé' && quote?.status !== 'en_production' && quote?.status !== 'terminé' && quote?.status !== 'annulé';

  // Admin-only: when generating PDF or email, optionally hide calculation details (show only grand total)

  const handleDownloadPDF = async (simple = false) => {
    try {
      const useSimple = isAdminView && simple;
      const url = useSimple ? `${API_URL}/quotes/${id}/pdf?simple=true` : `${API_URL}/quotes/${id}/pdf`;
      const response = await axios.get(url);
      const { pdf_base64, filename } = response.data;
      
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdf_base64}`;
      link.download = filename;
      link.click();
      
      toast.success(useSimple ? 'PDF simplifié téléchargé' : 'PDF détaillé téléchargé');
      return { pdf_base64, filename };
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
      return null;
    }
  };

  // Mode 1: Envoie un mail "détaillé" — tout le résumé dans le corps du mail (pas de PDF)
  const handleSendEmailDetailed = () => {
    if (!quote) return;
    const tvaRate = quote.tva_rate ?? 20;
    const showTVA = tvaRate > 0;

    // French money formatter with space thousands separator and comma decimal
    const fmt = (n) => {
      const v = Number(n ?? 0);
      return v.toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + ' €';
    };

    // Plans
    const plansLines = (quote.plans || []).map(p => {
      const surf = p.surface_m2 ?? ((p.length_mm * p.width_mm) / 1_000_000);
      const surfStr = surf.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
      return `- ${p.designation || 'Plan'} : ${Math.round(p.length_mm)} × ${Math.round(p.width_mm)} mm = ${surfStr} m²`;
    }).join('\n');

    // Façonnages : toujours quantité au début (ex "1 trou poli évier")
    const finishingsLines = (quote.finishings || []).map(f => {
      const qty = f.quantity ?? 1;
      // Mettre la 1ère lettre du nom en minuscule pour fluidité
      const name = (f.finishing_name || '').charAt(0).toLowerCase() + (f.finishing_name || '').slice(1);
      return `- ${qty} ${name}`;
    }).join('\n');

    // Chants
    const edgesLines = (quote.edges || []).map(e => {
      const lm = (e.length_ml ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return `- ${e.edge_type_name} : ${lm} ml`;
    }).join('\n');

    // Services (pas de mention "surcharge étage" dans le mail)
    const svc = quote.services || {};
    const lines = [];
    if (svc.installation_package) {
      lines.push('Forfait : prise de gabarit, livraison et pose'
        + (svc.installation_zone ? ` (${svc.installation_zone})` : ''));
      const ci = quote.client_info || {};
      if (ci.has_floor && ci.floor_number > 0) {
        const n = ci.floor_number;
        const suffix = n === 1 ? 'er' : 'e';
        const elev = ci.has_elevator ? 'avec ascenseur' : 'sans ascenseur';
        lines.push(`Lieu de pose : ${n}${suffix} étage ${elev}`);
      } else {
        lines.push('Lieu de pose : rez-de-chaussée');
      }
      if (svc.is_nous_consulter_floor) {
        lines.push('Prix de pose : Nous consulter (étage élevé)');
      }
    } else {
      lines.push('(pas de forfait pose sélectionné)');
    }
    const servicesText = lines.join('\n');

    // Récapitulatif
    const recapLines = [];
    recapLines.push(`Prix de l'ensemble hors taxe : ${fmt(quote.subtotal_ht)}`);
    if (showTVA) {
      recapLines.push(`TVA (${tvaRate}%) : ${fmt(quote.tva_amount)}`);
      recapLines.push(`Prix de l'ensemble TTC : ${fmt(quote.total_ttc)}`);
    } else {
      recapLines.push('TVA : Exonéré');
      recapLines.push(`Prix de l'ensemble : ${fmt(quote.subtotal_ht)}`);
    }
    const recapText = recapLines.join('\n');

    // Informations complémentaires (notes)
    const notesBlock = quote.notes && quote.notes.trim()
      ? `\nInformations complémentaires :\n${quote.notes.trim()}\n`
      : '';

    // Corps du mail : titres en MAJUSCULES (mailto plain text), pas de gros traits
    const surfaceTotaleStr = (quote.total_surface_m2 ?? 0).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const body =
`Bonjour ${quote.client_info.name},

Veuillez trouver ci-dessous le détail de votre devis n°${quote.quote_number}.

MATIÈRE SÉLECTIONNÉE :
${quote.material_name} (${quote.material_category})
Épaisseur : ${quote.thickness}

DIMENSIONS DÉTAILLÉES :
${plansLines || '- aucun plan'}
Surface totale : ${surfaceTotaleStr} m²
${finishingsLines ? `\nFAÇONNAGE :\n${finishingsLines}\n` : ''}${edgesLines ? `\nCHANTS VISIBLES :\n${edgesLines}\n` : ''}
SERVICES :
${servicesText}
${notesBlock}
RÉCAPITULATIF :
${recapText}

Ce devis est valable 30 jours.

N'hésitez pas à nous contacter pour toute question.

Cordialement,
L'équipe GRANIPAL
6 Rue de la porte écluse
91270 Vigneux-sur-Seine
Tél : 01 69 83 04 79
Email : granipal@orange.fr`;

    const subject = encodeURIComponent(`Devis GRANIPAL n°${quote.quote_number} — Détail`);
    const mailtoUrl = `mailto:${quote.client_info.email}?subject=${subject}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
    toast.info('Votre application mail s\'ouvre avec le détail du devis pré-rempli.');
  };

  // Mode 2: Envoie un mail simple + PDF
  // Tente d'attacher le PDF via Web Share API (Level 2). Fallback: téléchargement + mailto.
  const handleSendEmailWithPDF = async () => {
    if (!quote) return;
    const showTVA = (quote.tva_rate ?? 20) > 0;
    const tvaRate = quote.tva_rate ?? 20;

    const fmt = (n) => {
      const v = Number(n ?? 0);
      return v.toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + ' €';
    };

    const bodyLines = [];
    bodyLines.push(`Bonjour ${quote.client_info.name},`);
    bodyLines.push('');
    bodyLines.push(`Veuillez trouver ci-joint le devis n°${quote.quote_number}.`);
    bodyLines.push('');
    bodyLines.push(`Prix de l'ensemble hors taxe : ${fmt(quote.subtotal_ht)}`);
    if (showTVA) {
      bodyLines.push(`TVA (${tvaRate}%) : ${fmt(quote.tva_amount)}`);
      bodyLines.push(`Prix de l'ensemble TTC : ${fmt(quote.total_ttc)}`);
    } else {
      bodyLines.push('TVA : Exonéré');
    }
    bodyLines.push('');
    bodyLines.push('Ce devis est valable 30 jours.');
    bodyLines.push('');
    bodyLines.push('N\'hésitez pas à nous contacter pour toute question.');
    bodyLines.push('');
    bodyLines.push('Cordialement,');
    bodyLines.push('L\'équipe GRANIPAL');
    bodyLines.push('6 Rue de la porte écluse');
    bodyLines.push('91270 Vigneux-sur-Seine');
    bodyLines.push('Tél : 01 69 83 04 79');
    bodyLines.push('Email : granipal@orange.fr');
    const bodyText = bodyLines.join('\n');
    const subjectText = `Devis GRANIPAL n°${quote.quote_number}`;

    // Récupérer le PDF
    let pdfFile = null;
    let pdfBlobUrl = null;
    try {
      const pdfUrl = `${API_URL}/quotes/${id}/pdf`;
      const response = await axios.get(pdfUrl);
      const { pdf_base64, filename } = response.data;
      const byteChars = atob(pdf_base64);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
      pdfFile = new File([bytes], filename, { type: 'application/pdf' });
      pdfBlobUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    } catch (e) {
      toast.error('Impossible de récupérer le PDF');
      return;
    }

    // Tentative 1 : Web Share API niveau 2 (mobile + Chrome récent)
    try {
      if (navigator.canShare && pdfFile && navigator.canShare({ files: [pdfFile] })) {
        await navigator.share({
          title: subjectText,
          text: bodyText,
          files: [pdfFile],
        });
        toast.success('Partage du devis lancé avec le PDF joint.');
        if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
        return;
      }
    } catch (err) {
      // L'utilisateur a annulé OU le navigateur ne supporte pas → fallback
      if (err && err.name === 'AbortError') {
        if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
        return;
      }
      // continue au fallback
    }

    // Fallback : téléchargement + mailto (PJ à faire manuellement)
    const link = document.createElement('a');
    link.href = pdfBlobUrl;
    link.download = pdfFile.name;
    link.click();
    setTimeout(() => { if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl); }, 5000);

    const mailtoUrl = `mailto:${quote.client_info.email}?subject=${encodeURIComponent(subjectText)}&body=${encodeURIComponent(bodyText)}`;
    window.location.href = mailtoUrl;
    toast.info('PDF téléchargé. Glissez-le simplement dans le mail (votre navigateur ne supporte pas la pièce jointe automatique).');
  };

  const handleDuplicate = async () => {
    try {
      const response = await axios.post(`${API_URL}/quotes/${id}/duplicate`);
      toast.success(`Devis dupliqué: ${response.data.quote_number}`);
      navigate(`${isAdminView ? '/admin' : '/dashboard'}/devis/${response.data.id}`);
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleModify = () => {
    navigate(`/configurateur?duplicate=${id}`);
    toast.info('Modification en cours - un nouveau devis sera créé');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API_URL}/messages`, {
        quote_id: id,
        content: newMessage
      });
      setNewMessage('');
      fetchMessages();
      toast.success('Message envoyé');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Helper to get event type info (handles legacy types)
  const getEventTypeInfo = (event) => {
    const type = event.event_type === 'pose' || event.event_type === 'livraison' 
      ? 'livraison_pose' 
      : event.event_type;
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[2];
  };

  // Helper to get event display label
  const getEventDisplayLabel = (event) => {
    if (event.event_type === 'autre' && event.event_label) {
      return event.event_label;
    }
    const typeInfo = getEventTypeInfo(event);
    return typeInfo.label;
  };

  // Helper to get event time
  const getEventTime = (event) => {
    const dateStr = event.validated_date || event.proposed_date;
    if (dateStr?.includes('T')) {
      return dateStr.split('T')[1]?.substring(0, 5);
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!quote) return null;

  const basePath = isAdminView ? '/admin' : '/dashboard';

  return (
    <div className="space-y-6" data-testid="quote-detail">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to={`${basePath}/devis`}
            className="p-2 text-white/40 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white">
              Devis {quote.quote_number}
            </h1>
            <p className="text-white/50 text-sm">
              Créé le {new Date(quote.created_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {(isAdmin || isAdminView) && (
            <Select value={quote.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-40 bg-[#1C1C1C] border-white/10 text-white rounded-none" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1C1C1C] border-white/10">
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="text-white hover:bg-white/10">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Client PRO validation button */}
          {canClientValidate && (
            <button 
              onClick={() => setShowValidationDialog(true)} 
              className="btn-primary flex items-center gap-2 py-2 bg-green-600 hover:bg-green-700"
              data-testid="validate-quote-btn"
            >
              <Check className="w-4 h-4" />
              Valider le devis
            </button>
          )}
          
          {/* Locked badge if validated */}
          {quote.status === 'validé' && !isAdminView && (
            <span className="px-3 py-2 bg-green-500/20 text-green-400 text-sm flex items-center gap-2">
              <Check className="w-4 h-4" />
              Devis validé
            </span>
          )}
          
          <button onClick={handleModify} className="btn-primary flex items-center gap-2 py-2" data-testid="modify-btn">
            <Edit2 className="w-4 h-4" />
            Modifier
          </button>
          {/* PDF — bouton unique avec sous-menu (PDF détaillé / PDF simplifié pour admin) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="btn-secondary flex items-center gap-2 py-2"
                data-testid="download-pdf-btn"
                title="Télécharger le devis en PDF"
              >
                <Download className="w-4 h-4" />
                Télécharger PDF
                <ChevronDown className="w-3.5 h-3.5 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#1C1C1C] border-white/10 text-white min-w-[220px]"
              data-testid="download-pdf-menu"
            >
              <DropdownMenuItem
                onClick={() => handleDownloadPDF(false)}
                className="cursor-pointer focus:bg-white/10 focus:text-white"
                data-testid="download-pdf-detailed-option"
              >
                <FileText className="w-4 h-4 mr-2 text-[#D4AF37]" />
                <div className="flex flex-col">
                  <span className="font-medium">PDF détaillé</span>
                  <span className="text-xs text-white/50">Avec calculs complets</span>
                </div>
              </DropdownMenuItem>
              {(isAdmin || isAdminView) && (
                <>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem
                    onClick={() => handleDownloadPDF(true)}
                    className="cursor-pointer focus:bg-white/10 focus:text-white"
                    data-testid="download-pdf-simple-option"
                  >
                    <FileText className="w-4 h-4 mr-2 text-white/60" />
                    <div className="flex flex-col">
                      <span className="font-medium">PDF simplifié</span>
                      <span className="text-xs text-white/50">Détails techniques sans prix intermédiaires</span>
                    </div>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="btn-secondary flex items-center gap-2 py-2" data-testid="send-email-btn">
                <Mail className="w-4 h-4" />
                Envoyer par mail
                <ChevronDown className="w-3.5 h-3.5 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#1C1C1C] border border-white/10 text-white min-w-[280px]"
            >
              <DropdownMenuItem
                onClick={handleSendEmailDetailed}
                className="cursor-pointer focus:bg-[#D4AF37]/15 focus:text-white py-3"
                data-testid="send-email-detailed"
              >
                <FileText className="w-4 h-4 mr-2 text-[#D4AF37]" />
                <div className="flex flex-col">
                  <span className="font-medium">Envoyer le détail dans le mail</span>
                  <span className="text-xs text-white/50">Résumé complet pré-rempli</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/5" />
              <DropdownMenuItem
                onClick={handleSendEmailWithPDF}
                className="cursor-pointer focus:bg-[#D4AF37]/15 focus:text-white py-3"
                data-testid="send-email-pdf"
              >
                <Download className="w-4 h-4 mr-2 text-[#D4AF37]" />
                <div className="flex flex-col">
                  <span className="font-medium">Envoyer le PDF</span>
                  <span className="text-xs text-white/50">Téléchargement + mail à joindre</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={handleDuplicate} className="btn-secondary flex items-center gap-2 py-2" data-testid="duplicate-btn">
            <Copy className="w-4 h-4" />
            Dupliquer
          </button>
        </div>
      </div>

      {/* Status Badge */}
      {!isAdmin && !isAdminView && (
        <div className={`inline-block status-${quote.status} px-4 py-2 text-sm uppercase tracking-wider`}>
          {statusOptions.find(s => s.value === quote.status)?.label}
        </div>
      )}

      {/* Small Surface Warning */}
      {quote.total_surface_m2 < 2 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-500" />
          <p className="text-yellow-500 text-sm">
            Surface inférieure à 2,00 m² – tarif spécifique, nous consulter
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Info */}
          <div className="card-dashboard">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="text-lg font-semibold text-white">Client</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-white font-medium">{quote.client_info.name}</p>
                <p className="text-white/50">{quote.client_info.email}</p>
                <p className="text-white/50">{quote.client_info.phone}</p>
              </div>
              <div>
                <p className="text-white/50">{quote.client_info.address}</p>
                <p className="text-white/50">{quote.client_info.postal_code} {quote.client_info.city}</p>
                {quote.client_info.has_floor && (
                  <p className="text-white/50">
                    Étage {quote.client_info.floor_number} 
                    {quote.client_info.has_elevator ? ' (avec ascenseur)' : ' (sans ascenseur)'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Material */}
          <div className="card-dashboard">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="text-lg font-semibold text-white">Matière</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-white font-medium">{quote.material_name}</p>
                <p className="text-white/50 text-sm">{quote.material_category}</p>
              </div>
              <div className="text-sm">
                <p className="text-white/50">Épaisseur: <span className="text-white">{quote.thickness}</span></p>
                <p className="text-white/50">Prix/m²: <span className="text-white">{quote.material_price_per_m2} €</span></p>
              </div>
            </div>
          </div>

          {/* Dimensions */}
          <div className="card-dashboard">
            <div className="flex items-center gap-2 mb-4">
              <Ruler className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="text-lg font-semibold text-white">Dimensions</h2>
            </div>
            <div className="space-y-3">
              {quote.plans?.map((plan, index) => {
                const length = plan.length_mm || (plan.length_cm ? plan.length_cm * 10 : 0);
                const width = plan.width_mm || (plan.width_cm ? plan.width_cm * 10 : 0);
                const designation = plan.designation || `Plan ${index + 1}`;
                return (
                  <div key={plan.id || index} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-white/50 text-sm">{designation}</span>
                    <span className="text-white">{Math.round(length)} × {Math.round(width)} mm</span>
                    <span className="text-white/70">{plan.surface_m2?.toFixed(2)} m²</span>
                  </div>
                );
              })}
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <span className="text-white font-medium">Surface totale</span>
                <span className="text-[#D4AF37] font-bold">{quote.total_surface_m2?.toFixed(2)} m²</span>
              </div>
              {quote.width_surcharge_applied && (
                <div className="flex items-center justify-between text-yellow-500 text-sm">
                  <span>Plus-value largeur {'>'}700mm (+30%)</span>
                  <span>+{quote.width_surcharge_amount?.toFixed(2)} €</span>
                </div>
              )}
            </div>
          </div>

          {/* Finishings - Table format */}
          {quote.finishings?.length > 0 && (
            <div className="card-dashboard">
              <div className="flex items-center gap-2 mb-4">
                <Wrench className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="text-lg font-semibold text-white">Façonnage</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-white/50">
                      <th className="text-left py-2">Désignation</th>
                      <th className="text-center py-2">Quantité</th>
                      <th className="text-right py-2">Prix unitaire</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.finishings.map((f, index) => (
                      <tr key={`${f.finishing_name}-${index}`} className="border-b border-white/5">
                        <td className="py-2 text-white">{f.finishing_name}</td>
                        <td className="py-2 text-center text-white/70">{f.quantity}</td>
                        <td className="py-2 text-right text-white/70">{f.unit_price} €</td>
                        <td className="py-2 text-right text-white font-medium">{f.total?.toFixed(2)} €</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-white/10">
                      <td colSpan="3" className="py-2 text-white font-medium">Total façonnage</td>
                      <td className="py-2 text-right text-white font-bold">{quote.finishings_total?.toFixed(2)} €</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Edges - Table format */}
          {quote.edges?.length > 0 && (
            <div className="card-dashboard">
              <div className="flex items-center gap-2 mb-4">
                <Square className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="text-lg font-semibold text-white">Chants</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-white/50">
                      <th className="text-left py-2">Désignation</th>
                      <th className="text-center py-2">Longueur</th>
                      <th className="text-right py-2">Prix/ml</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.edges.map((e, index) => (
                      <tr key={`${e.edge_type_name}-${index}`} className="border-b border-white/5">
                        <td className="py-2 text-white">{e.edge_type_name}</td>
                        <td className="py-2 text-center text-white/70">{e.length_ml} ml</td>
                        <td className="py-2 text-right text-white/70">{e.price_per_ml} €</td>
                        <td className="py-2 text-right text-white font-medium">{e.total?.toFixed(2)} €</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-white/10">
                      <td colSpan="3" className="py-2 text-white font-medium">Total chants</td>
                      <td className="py-2 text-right text-white font-bold">{quote.edges_total?.toFixed(2)} €</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Services - Simplified display: only show total */}
          {quote.services_total > 0 && (
            <div className="card-dashboard">
              <div className="flex items-center gap-2 mb-4">
                <Truck className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="text-lg font-semibold text-white">Services</h2>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 text-sm">
                  <span className="text-white">Forfait (Gabarit + Livraison + Pose)</span>
                  <span className="text-white font-bold">{quote.services_total?.toFixed(2)} €</span>
                </div>
                {quote.services?.delivery_city && (
                  <p className="text-white/50 text-xs">
                    Zone: {quote.services.installation_zone || quote.services.delivery_city}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Informations complémentaires (notes libres) */}
          {quote.notes && quote.notes.trim() && (
            <div className="card-dashboard" data-testid="quote-notes-display">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="text-lg font-semibold text-white">Informations complémentaires</h2>
              </div>
              <p className="text-white/80 text-sm whitespace-pre-wrap leading-relaxed">
                {quote.notes}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Totals */}
          <div className="card-dashboard bg-[#1C1C1C]">
            <h2 className="text-lg font-semibold text-white mb-4">Récapitulatif</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-white/50">Matière</span>
                <span className="text-white">{quote.material_total?.toFixed(2)} €</span>
              </div>
              {quote.finishings_total > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/50">Façonnage</span>
                  <span className="text-white">{quote.finishings_total?.toFixed(2)} €</span>
                </div>
              )}
              {quote.edges_total > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/50">Chants</span>
                  <span className="text-white">{quote.edges_total?.toFixed(2)} €</span>
                </div>
              )}
              {quote.services_total > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/50">Services</span>
                  <span className="text-white">{quote.services_total?.toFixed(2)} €</span>
                </div>
              )}
              
              {/* Discount display */}
              {quote.discount_amount > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>Remise ({quote.discount_type === 'percentage' ? `${quote.discount_value}%` : 'fixe'})</span>
                  <span>-{quote.discount_amount?.toFixed(2)} €</span>
                </div>
              )}

              {/* Adjustments (admin view) — always visible in the platform detail */}
              {(isAdmin || isAdminView) && Array.isArray(quote.adjustments) && quote.adjustments.length > 0 && (
                <div className="border-t border-white/5 pt-2 space-y-1">
                  {quote.adjustments.map((adj, i) => (
                    <div key={`adj-${i}`} className={`flex justify-between items-center text-sm ${(adj.amount_ht || 0) >= 0 ? 'text-[#D4AF37]' : 'text-orange-400'}`}>
                      <span className="flex items-center gap-2">
                        <span className="truncate max-w-[260px]">{adj.description || 'Ajustement'}</span>
                        {adj.show_on_pdf === false && (
                          <span className="text-[10px] uppercase tracking-wider text-white/30 border border-white/15 px-1.5 py-0.5">PDF masqué</span>
                        )}
                      </span>
                      <span className="font-medium">{(adj.amount_ht || 0) >= 0 ? '+' : ''}{(adj.amount_ht || 0).toFixed(2)} €</span>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="border-t border-white/10 pt-3">
                <div className="flex justify-between">
                  <span className="text-white/50">Total HT</span>
                  <span className="text-white font-medium">{quote.subtotal_ht?.toFixed(2)} €</span>
                </div>
                {quote.tva_rate > 0 ? (
                  <div className="flex justify-between mt-1">
                    <span className="text-white/50">TVA ({quote.tva_rate}%)</span>
                    <span className="text-white">{quote.tva_amount?.toFixed(2)} €</span>
                  </div>
                ) : (
                  <div className="flex justify-between mt-1">
                    <span className="text-white/50">TVA</span>
                    <span className="text-white/60">Exonéré</span>
                  </div>
                )}
              </div>
              <div className="border-t border-[#D4AF37]/30 pt-3">
                <div className="flex justify-between">
                  <span className="text-white font-bold">{quote.tva_rate > 0 ? 'Total TTC' : 'Total HT'}</span>
                  <span className="text-[#D4AF37] text-xl font-bold">
                    {quote.tva_rate > 0 ? quote.total_ttc?.toFixed(2) : quote.subtotal_ht?.toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="card-dashboard">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="text-lg font-semibold text-white">Messages</h2>
            </div>
            
            <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
              {messages.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-4">Aucun message</p>
              ) : (
                messages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`p-3 text-sm ${
                      msg.sender_role === 'admin' 
                        ? 'bg-[#5D1F1F]/20 border-l-2 border-[#5D1F1F]' 
                        : 'bg-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white/70 font-medium text-xs">{msg.sender_name}</span>
                      <span className="text-white/40 text-xs">
                        {new Date(msg.created_at).toLocaleString('fr-FR', { 
                          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                        })}
                      </span>
                    </div>
                    <p className="text-white/80">{msg.content}</p>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Votre message..."
                className="flex-1 bg-[#0A0A0A] border-white/10 text-white rounded-none text-sm"
                data-testid="message-input"
              />
              <button
                type="submit"
                disabled={sendingMessage || !newMessage.trim()}
                className="p-2 bg-[#5D1F1F] text-white disabled:opacity-50"
                data-testid="send-message-btn"
              >
                {sendingMessage ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </form>
          </div>

          {/* Planning Section - Updated */}
          <div className="card-dashboard">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="text-lg font-semibold text-white">Planning</h2>
              </div>
              <button
                onClick={() => {
                  resetEventForm();
                  setShowPlanningDialog(true);
                }}
                className="btn-secondary text-sm py-2 flex items-center gap-1"
                data-testid="add-event-btn"
              >
                <Plus className="w-4 h-4" />
                Proposer une date
              </button>
            </div>

            {planningEvents.length === 0 ? (
              <p className="text-white/40 text-sm text-center py-4">
                Aucune date planifiée. Proposez une date pour le relevé ou la pose.
              </p>
            ) : (
              <div className="space-y-3">
                {planningEvents.map((event) => {
                  const eventType = getEventTypeInfo(event);
                  const timeStr = getEventTime(event);
                  return (
                    <div 
                      key={event.id}
                      className="p-3 bg-[#0A0A0A] border border-white/10"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div 
                            className="w-2 h-10 rounded-sm mt-1"
                            style={{ backgroundColor: eventType?.color || '#6B7280' }}
                          />
                          <div>
                            <p className="text-white font-medium">{getEventDisplayLabel(event)}</p>
                            <p className="text-white/50 text-sm">
                              {event.validated_date 
                                ? new Date(event.validated_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
                                : new Date(event.proposed_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
                              }
                              {timeStr && (
                                <span className="ml-2 text-[#D4AF37]">
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  {timeStr}
                                </span>
                              )}
                            </p>
                            {event.comment && (
                              <p className="text-white/40 text-xs mt-1">{event.comment}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`text-xs px-2 py-1 ${
                            event.status === 'validated' 
                              ? 'bg-green-500/20 text-green-400' 
                              : event.status === 'modified_by_admin'
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {STATUS_LABELS[event.status] || 'Proposée'}
                          </span>
                          <div className="flex items-center gap-1">
                            {isAdminView && event.status === 'proposed' && (
                              <button
                                onClick={() => handleValidateEvent(event.id)}
                                className="p-1 text-green-400 hover:bg-green-500/10 rounded"
                                title="Valider"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => openEditDialog(event)}
                              className="p-1 text-white/50 hover:text-white hover:bg-white/10 rounded"
                              title="Modifier"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteEvent(event.id)}
                              className="p-1 text-red-400 hover:bg-red-500/10 rounded"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Planning Dialog - Updated with time */}
      <Dialog open={showPlanningDialog} onOpenChange={(open) => {
        if (!open) resetEventForm();
        setShowPlanningDialog(open);
      }}>
        <DialogContent className="bg-[#1C1C1C] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingEvent ? 'Modifier l\'événement' : 'Proposer une date'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-white/60 text-sm mb-2 block">Type d'événement</label>
              <Select 
                value={newEvent.event_type} 
                onValueChange={(v) => setNewEvent({...newEvent, event_type: v, event_label: ''})}
                disabled={!!editingEvent}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1C] border-white/10">
                  {EVENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/10">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: type.color }} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom label for "Autre" type */}
            {newEvent.event_type === 'autre' && (
              <div>
                <label className="text-white/60 text-sm mb-2 block">Préciser le type *</label>
                <Input
                  value={newEvent.event_label}
                  onChange={(e) => setNewEvent({...newEvent, event_label: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                  placeholder="Ex: SAV, Contrôle chantier, Retouche..."
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#D4AF37]" />
                  Date *
                </label>
                <div className="relative">
                  <Input
                    type="date"
                    value={newEvent.proposed_date}
                    onChange={(e) => setNewEvent({...newEvent, proposed_date: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none pl-10"
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="event-date"
                  />
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                </div>
              </div>
              <div>
                <label className="text-white/60 text-sm mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#D4AF37]" />
                  Heure
                </label>
                <div className="relative">
                  <Input
                    type="time"
                    value={newEvent.proposed_time}
                    onChange={(e) => setNewEvent({...newEvent, proposed_time: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none pl-10"
                    data-testid="event-time"
                  />
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                </div>
              </div>
            </div>

            <div>
              <label className="text-white/60 text-sm mb-2 block">Commentaire (optionnel)</label>
              <Input
                value={newEvent.comment}
                onChange={(e) => setNewEvent({...newEvent, comment: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                placeholder="Ex: Meubles posés semaine prochaine..."
                data-testid="event-comment"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button onClick={() => setShowPlanningDialog(false)} className="btn-secondary flex-1">
                Annuler
              </button>
              <button 
                onClick={editingEvent ? handleUpdateEvent : handleAddEvent} 
                className="btn-primary flex-1"
              >
                {editingEvent ? 'Modifier' : 'Proposer'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Validation Confirmation Dialog */}
      <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
        <DialogContent className="bg-[#1C1C1C] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              Confirmer la validation
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-white/80">
              Confirmez-vous la validation de ce devis ?
            </p>
            <p className="text-white/50 text-sm">
              Une fois validé, le devis ne pourra plus être modifié. GRANIPAL sera notifié de votre confirmation.
            </p>
            
            <div className="p-3 bg-[#0A0A0A] border border-white/10">
              <p className="text-white font-medium">Devis n° {quote?.quote_number}</p>
              <p className="text-[#D4AF37] text-lg font-bold mt-1">
                {quote?.tva_rate > 0 ? quote?.total_ttc?.toFixed(2) : quote?.subtotal_ht?.toFixed(2)} € {quote?.tva_rate > 0 ? 'TTC' : 'HT'}
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button onClick={() => setShowValidationDialog(false)} className="btn-secondary flex-1">
                Annuler
              </button>
              <button 
                onClick={handleClientValidation} 
                className="btn-primary flex-1 bg-green-600 hover:bg-green-700"
              >
                <Check className="w-4 h-4 mr-2 inline" />
                Oui, valider le devis
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
